import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Camera, CameraOff, Send, ArrowLeft, Volume2, VolumeX,
  ScanBarcode, Settings2, X, CheckCircle2, Scissors, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Html5Qrcode } from 'html5-qrcode';

interface TemplateInfo {
  id: string;
  name: string;
  fields: string[];
  fieldConfig?: Record<string, { lastChars?: number; stripChars?: string }>;
}

interface StripConfig {
  [field: string]: string; // characters to strip per field
}

interface LastCharsConfig {
  [field: string]: number; // take last X chars per field, 0 = disabled
}

interface PrefixOption {
  value: string;
  record: Record<string, string>;
}


const ScanPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Template state
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [fieldData, setFieldData] = useState<Record<string, string>>({});
  const [stripChars, setStripChars] = useState<StripConfig>({});
  const [lastCharsConfig, setLastCharsConfig] = useState<LastCharsConfig>({});
  const [prefixOptions, setPrefixOptions] = useState<Record<string, PrefixOption[]>>({});
  const [prefixOpenField, setPrefixOpenField] = useState<string | null>(null);

  // Scanner state
  const [scanning, setScanning] = useState(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef<string>('');
  const lastScanTimeRef = useRef<number>(0);

  // Sending state
  const [sending, setSending] = useState(false);

  const currentTemplate = templates.find(t => t.id === selectedTemplate);
  const currentFields = currentTemplate?.fields || [];

  // Load templates
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('templates').select('id, name, elements, field_config');
      if (data) {
        const parsed = data.map(t => {
          const fields = new Set<string>();
          const elements = t.elements as any[];
          elements?.forEach((el: any) => {
            if (el.content && typeof el.content === 'string') {
              const matches = el.content.matchAll(/\{\{(\w+)\}\}/g);
              for (const m of matches) fields.add(m[1]);
            }
            if (el.field) fields.add(el.field);
          });
          return {
            id: t.id,
            name: t.name,
            fields: Array.from(fields),
            fieldConfig: (t.field_config as any) || undefined,
          };
        }).filter(t => t.fields.length > 0);
        setTemplates(parsed);
      }
    };
    load();
  }, []);

  // Reset field data when template changes + load prefix options from datasets
  useEffect(() => {
    if (currentFields.length > 0) {
      const initial: Record<string, string> = {};
      currentFields.forEach(f => { initial[f] = ''; });
      setFieldData(initial);

      // Load saved field config from template
      if (currentTemplate?.fieldConfig) {
        const savedLastChars: LastCharsConfig = {};
        const savedStripChars: StripConfig = {};
        for (const [f, cfg] of Object.entries(currentTemplate.fieldConfig)) {
          if (cfg.lastChars) savedLastChars[f] = cfg.lastChars;
          if (cfg.stripChars) savedStripChars[f] = cfg.stripChars;
        }
        setLastCharsConfig(savedLastChars);
        setStripChars(savedStripChars);
      } else {
        setLastCharsConfig({});
        setStripChars({});
      }

      // Load prefix options from datasets for each field
      const loadPrefixes = async () => {
        const { data: datasets } = await supabase.from('data_datasets').select('id, fields');
        if (!datasets) return;

        const allPrefixes: Record<string, PrefixOption[]> = {};
        for (const f of currentFields) {
          const matchingIds = datasets
            .filter(d => (d.fields as string[] || []).includes(f))
            .map(d => d.id);
          if (matchingIds.length === 0) continue;

          const { data: records } = await supabase
            .from('data_records')
            .select('field_data')
            .in('dataset_id', matchingIds);

          if (records) {
            const seen = new Set<string>();
            allPrefixes[f] = records
              .map(r => {
                const fd = r.field_data as Record<string, string>;
                return { value: fd[f] || '', record: fd };
              })
              .filter(o => {
                if (!o.value || seen.has(o.value)) return false;
                seen.add(o.value);
                return true;
              });
          }
        }
        setPrefixOptions(allPrefixes);
      };
      loadPrefixes();
    }
  }, [selectedTemplate]);

  const applyStripChars = useCallback((value: string, field: string): string => {
    const chars = stripChars[field];
    if (!chars) return value;
    // Remove each character in the strip string
    let result = value;
    for (const ch of chars) {
      result = result.split(ch).join('');
    }
    return result;
  }, [stripChars]);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1200;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch {}
  }, [soundEnabled]);

  const handleScan = useCallback((decodedText: string) => {
    const now = Date.now();
    if (decodedText === lastScanRef.current && now - lastScanTimeRef.current < 2000) return;
    lastScanRef.current = decodedText;
    lastScanTimeRef.current = now;

    if (!activeField) {
      toast.error('Select a field to scan into first');
      return;
    }

    playBeep();
    let result = applyStripChars(decodedText, activeField);
    // Apply last X chars if configured
    const lc = lastCharsConfig[activeField] || 0;
    if (lc > 0 && result.length > lc) {
      result = result.slice(-lc);
    }
    setFieldData(prev => ({ ...prev, [activeField]: result }));
    toast.success(`${activeField}: ${result.substring(0, 40)}`);
  }, [playBeep, activeField, applyStripChars, lastCharsConfig]);

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;
    try {
      const scanner = new Html5Qrcode('scanner-region');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 1.5 },
        handleScan,
        () => {}
      );
      setScanning(true);
    } catch (err: any) {
      toast.error('Camera access denied or unavailable');
      console.error('Scanner error:', err);
    }
  }, [handleScan]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  // Restart scanner when handleScan changes (activeField changed)
  useEffect(() => {
    if (scanning && scannerRef.current) {
      // Stop and restart to pick up new handleScan
      const restart = async () => {
        await stopScanner();
        // Small delay to let DOM settle
        setTimeout(() => startScanner(), 300);
      };
      restart();
    }
  }, [activeField]); // intentionally only activeField

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const updateField = (field: string, value: string) => {
    setFieldData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendLabel = useCallback(async () => {
    if (!user || !selectedTemplate || !currentTemplate) {
      toast.error('Select a template first');
      return;
    }

    // Check at least one field is filled
    const hasData = Object.values(fieldData).some(v => v.trim());
    if (!hasData) {
      toast.error('Fill at least one field');
      return;
    }

    setSending(true);
    const { error } = await supabase.from('scan_queue' as any).insert([{
      user_id: user.id,
      barcode_value: Object.values(fieldData).find(v => v.trim()) || '',
      template_id: selectedTemplate,
      template_name: currentTemplate.name,
      field_name: currentFields[0] || '',
      field_data: fieldData,
      strip_chars: stripChars,
      status: 'pending',
    }] as any);

    setSending(false);

    if (error) {
      toast.error(`Failed to send: ${error.message}`);
      console.error('scan_queue insert error:', error);
      return;
    }

    toast.success('Label sent for review!');
    // Reset fields
    const reset: Record<string, string> = {};
    currentFields.forEach(f => { reset[f] = ''; });
    setFieldData(reset);
  }, [user, selectedTemplate, currentTemplate, fieldData, stripChars, currentFields]);

  const filledCount = Object.values(fieldData).filter(v => v.trim()).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold text-foreground flex-1">Mobile Scanner</h1>
        <Button variant="ghost" size="icon" onClick={() => setSoundEnabled(!soundEnabled)}>
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </Button>
      </header>

      {/* Template Selection */}
      <div className="px-4 py-3 bg-card border-b border-border shrink-0">
        <Select value={selectedTemplate} onValueChange={v => { setSelectedTemplate(v); setActiveField(null); }}>
          <SelectTrigger className="h-10">
            <SelectValue placeholder="Select template..." />
          </SelectTrigger>
          <SelectContent>
            {templates.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 pb-24">
          {!selectedTemplate && (
            <div className="text-center text-muted-foreground py-12 space-y-2">
              <ScanBarcode className="w-12 h-12 mx-auto opacity-40" />
              <p className="text-sm">Select a template to start scanning and filling fields.</p>
            </div>
          )}

          {selectedTemplate && currentFields.length > 0 && (
            <>
              {/* Camera */}
              <Card>
                <CardContent className="p-3 space-y-3">
                  <div
                    id="scanner-region"
                    className={cn(
                      "w-full rounded-lg overflow-hidden bg-muted border-2",
                      scanning ? "border-primary" : "border-border"
                    )}
                    style={{ minHeight: scanning ? 240 : 0 }}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={scanning ? stopScanner : startScanner}
                      className="flex-1 h-10"
                      variant={scanning ? "destructive" : "default"}
                    >
                      {scanning ? (
                        <><CameraOff className="w-4 h-4 mr-2" /> Stop</>
                      ) : (
                        <><Camera className="w-4 h-4 mr-2" /> Start Camera</>
                      )}
                    </Button>
                  </div>
                  {scanning && activeField && (
                    <p className="text-xs text-center text-muted-foreground">
                      Scanning into: <span className="font-semibold text-foreground">{activeField}</span>
                    </p>
                  )}
                  {scanning && !activeField && (
                    <p className="text-xs text-center text-destructive">
                      Tap a field below to select where the scan goes
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Fields */}
              <div className="space-y-3">
                {currentFields.map(field => (
                  <Card
                    key={field}
                    className={cn(
                      "transition-colors",
                      activeField === field && "ring-2 ring-primary"
                    )}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{field}</Label>
                        <div className="flex items-center gap-1">
                          {/* Settings: strip chars + last X chars */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <Settings2 className="w-3.5 h-3.5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64" align="end">
                              <div className="space-y-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Zeichen entfernen</Label>
                                  <Input
                                    placeholder="z.B. -_/ (zu entfernende Zeichen)"
                                    value={stripChars[field] || ''}
                                    onChange={e => setStripChars(prev => ({ ...prev, [field]: e.target.value }))}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Letzte X Zeichen</Label>
                                  <Input
                                    type="number"
                                    min={0}
                                    placeholder="0 = deaktiviert"
                                    value={lastCharsConfig[field] || ''}
                                    onChange={e => setLastCharsConfig(prev => ({ ...prev, [field]: parseInt(e.target.value) || 0 }))}
                                    className="h-8 text-sm"
                                  />
                                  <p className="text-[10px] text-muted-foreground">
                                    Nimmt nur die letzten X Zeichen des gescannten Werts.
                                  </p>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                          {/* Scan into this field button */}
                          <Button
                            variant={activeField === field ? "default" : "outline"}
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setActiveField(activeField === field ? null : field)}
                          >
                            <ScanBarcode className="w-3 h-3 mr-1" />
                            {activeField === field ? 'Aktiv' : 'Scan'}
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Input
                          value={fieldData[field] || ''}
                          onChange={e => updateField(field, e.target.value)}
                          placeholder={`${field} eingeben...`}
                          className="h-9 text-sm flex-1"
                        />
                        {/* Trim to last X chars button */}
                        {(lastCharsConfig[field] || 0) > 0 && (fieldData[field]?.length || 0) > (lastCharsConfig[field] || 0) && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            title={`Letzte ${lastCharsConfig[field]} Zeichen`}
                            onClick={() => {
                              const val = fieldData[field] || '';
                              updateField(field, val.slice(-(lastCharsConfig[field] || 0)));
                            }}
                          >
                            <Scissors className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Prefix dropdown from dataset */}
                        {(prefixOptions[field]?.length || 0) > 0 && (
                          <Popover open={prefixOpenField === field} onOpenChange={open => setPrefixOpenField(open ? field : null)}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 shrink-0"
                                title="Präfix voranstellen"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-0" align="end">
                              <div className="p-2 border-b border-border">
                                <p className="text-[10px] text-muted-foreground px-1">Wert voranstellen</p>
                              </div>
                              <ScrollArea className="max-h-48">
                                <div className="py-1">
                                  {prefixOptions[field]?.map((opt, i) => (
                                    <button
                                      key={i}
                                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
                                      onClick={() => {
                                        updateField(field, opt.value + (fieldData[field] || ''));
                                        setPrefixOpenField(null);
                                      }}
                                    >
                                      {opt.value} → <span className="text-muted-foreground">{opt.value}{fieldData[field] || ''}</span>
                                    </button>
                                  ))}
                                </div>
                              </ScrollArea>
                            </PopoverContent>
                          </Popover>
                        )}
                        {fieldData[field] && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={() => updateField(field, '')}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {(stripChars[field] || lastCharsConfig[field]) ? (
                        <p className="text-[10px] text-muted-foreground">
                          {stripChars[field] && <>Strip: <code className="bg-muted px-1 rounded">{stripChars[field]}</code> </>}
                          {(lastCharsConfig[field] || 0) > 0 && <>Letzte {lastCharsConfig[field]} Zeichen</>}
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Bottom Send Bar */}
      {selectedTemplate && currentFields.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border px-4 py-3 safe-area-bottom">
          <Button
            onClick={handleSendLabel}
            disabled={sending || filledCount === 0}
            className="w-full h-12 text-base"
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send Label ({filledCount}/{currentFields.length} fields)
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScanPage;
